import allLocales from "@fullcalendar/core/locales-all";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import { useMemo, useState } from "react";
import { Spinner } from "react-bootstrap";

import { useSession } from "../../../../contexts/SessionContext";
import { formatHourRange } from "../../../../services/TimeService";
import { MeetingCalendarProps } from "./MeetingCalendar";
import "./MeetingCalendar.scss";

const mapMeetingsToEvents = (meetings: any[] = []) =>
  meetings.map((meeting) => ({
    id: meeting.id,
    title: meeting.name,
    start: new Date(meeting.dt_start),
    end: new Date(meeting.dt_end),
    url: `/meeting/${meeting.id}`,
    extendedProps: {
      type: meeting.type.name,
      animators: meeting.animators?.length
        ? meeting.animators.map((animator: any) => animator.full_name).join(", ")
        : null,
      visibility: meeting.visibility,
    },
  }));

const MeetingsCalendar = ({ meetings = [], loading = false, onEventClick, onDatesChange }: MeetingCalendarProps) => {
  const { language } = useSession();
  const _formatTime = (date: Date | null) =>
    date?.toLocaleTimeString(language, { hour: "2-digit", minute: "2-digit" }) ?? "";
  const [tooltip, setTooltip] = useState<{
    title: string;
    type?: string;
    animators?: string;
    hoursrange: string;
    x: number;
    y: number;
  } | null>(null);

  const _events = useMemo(() => mapMeetingsToEvents(meetings), [meetings]);
  return (
    <div className="dst-calendar position-relative">
      {loading && (
        <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-white bg-opacity-50 z-3">
          <Spinner animation="border" />
        </div>
      )}

      <FullCalendar
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
        initialView="timeGridWeek"
        locale={language}
        locales={allLocales}
        allDaySlot={false}
        eventClassNames={() => ["clickable"]}
        dayMaxEvents={5}
        firstDay={1}
        headerToolbar={{
          left: "prev,next today",
          center: "title",
          right: "dayGridMonth,timeGridWeek,timeGridDay",
        }}
        events={_events}
        datesSet={(dateInfo) => onDatesChange?.(dateInfo.start, dateInfo.end)}
        eventContent={(arg) => {
          const startTime = arg.view.type === "dayGridMonth" && arg.event.start ? _formatTime(arg.event.start) : null;
          return (
            <div className="fc-event-title">
              {startTime && <span className="opacity-75">{startTime}</span>} - {arg.event.title}
            </div>
          );
        }}
        eventClick={(info) => {
          if (info.event.url) {
            info.jsEvent.preventDefault();
            window.open(info.event.url, "_blank");
          }
        }}
        eventMouseEnter={(info) => {
          const rect = info.el.getBoundingClientRect();
          setTooltip({
            title: info.event.title,
            type: info.event.extendedProps.type,
            animators: info.event.extendedProps.animators,
            hoursrange: formatHourRange(info.event.start!.toISOString(), info.event.end!.toISOString()),
            x: rect.left + rect.width / 2,
            y: rect.top,
          });
        }}
        eventDidMount={(info) => {
          info.el.addEventListener("mouseleave", () => setTooltip(null));
          return () => {
            info.el.removeEventListener("mouseleave", () => setTooltip(null));
          };
        }}
        height="70vh"
      />
      {tooltip && (
        <div
          className="dst-fc-tooltip position-fixed"
          style={{
            top: tooltip.y - 10,
            left: tooltip.x,
          }}
        >
          <div className="dst-fc-tooltip__inner bg-dark text-white p-2 rounded shadow-sm position-relative small">
            <div className="fw-bold">{tooltip.title}</div>
            {tooltip.type && <div>{tooltip.type}</div>}
            <div className="opacity-75">{tooltip.hoursrange}</div>
            {tooltip.animators && <div>{tooltip.animators}</div>}
          </div>
        </div>
      )}
    </div>
  );
};

export default MeetingsCalendar;
